import { X, ChevronDown } from 'lucide-react';
import { FC, useState, useRef, useEffect } from 'react';

type MultiSelectFilterProps = {
    value: string[];
    onChange: (values: string[]) => void;
    options: { value: string; label: string }[];
    id: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
};

const MultiSelectFilter: FC<MultiSelectFilterProps> = ({
    value = [],
    onChange,
    options,
    id,
    placeholder = 'Select...',
    disabled = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const handleClear = () => {
        onChange([]);
    };

    const getOptionValue = (option: { value: string; label: string }) => option.value;
    const getOptionLabel = (option: { value: string; label: string }) => option.label;

    const getSelectedLabels = () => {
        return value
            .map((val) => options.find((opt) => opt.value === val)?.label)
            .filter(Boolean)
            .join(', ');
    };

    return (
        <div className={`relative flex-1 ${className}`} ref={dropdownRef}>
            <div
                className={`form-select border-slate-200 focus:border-custom-500 focus:outline-none disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 cursor-pointer flex items-center justify-between ${
                    value.length > 0 ? 'text-slate-900' : 'text-slate-400'
                }`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span className="truncate">
                    {value.length > 0 ? (
                        <span className="flex items-center gap-1 flex-wrap">
                            {value.length} selected
                            {value.length <= 3 && (
                                <span className="text-xs text-slate-500">({getSelectedLabels()})</span>
                            )}
                        </span>
                    ) : (
                        placeholder
                    )}
                </span>
                <div className="flex items-center gap-1">
                    {value.length > 0 && !disabled && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                            className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                            aria-label="Clear selection"
                        >
                            <X className="size-4 text-slate-500" />
                        </button>
                    )}
                    <ChevronDown
                        className={`size-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>
            </div>
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2">
                        {options.map((option) => (
                            <label
                                key={option.value}
                                className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer rounded"
                            >
                                <input
                                    type="checkbox"
                                    checked={value.includes(option.value)}
                                    onChange={() => handleToggle(option.value)}
                                    className="w-4 h-4 text-custom-500 border-slate-300 rounded focus:ring-custom-500"
                                />
                                <span className="text-sm text-slate-900">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectFilter;

